import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Renderer2,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  ApplicationConfig,
  FlyoutLangSelectorFacade,
  HeaderFooterMachCore,
  HeaderFooterMachCoreContentService,
  LanguageRedirectionFacadeService,
  LanguageRedirectionHandlerService,
  LoginModalService,
  NavSlimService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Subject } from 'rxjs';
import { BookingFlightsFacadeService } from '../../../data-access/facades/booking-flights/booking-flights-facade.service';
import {
  AccountManagementService,
  BodyScrollService,
  BookingTabService,
  ComponentLoaderService,
  ConfigurationService,
  DarkSiteThemeService,
  DomManipulationService,
  HeaderStateService,
  LoginStateService,
  NavigationService,
  ScreenDetectionService
} from './services';

@Component({
  template: '',
  standalone: true
})
export class HeaderBaseExtendedComponent {
  readonly headerState = inject(HeaderStateService);
  readonly screenDetection = inject(ScreenDetectionService);
  readonly accountManagement = inject(AccountManagementService);
  readonly domManipulation = inject(DomManipulationService);
  readonly configuration = inject(ConfigurationService);
  readonly navigation = inject(NavigationService);
  readonly componentLoader = inject(ComponentLoaderService);
  readonly analytics = inject(AnalyticsService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly renderer = inject(Renderer2);
  readonly transSitesLangService = inject(LanguageRedirectionHandlerService);
  readonly languageRedirectionFacade = inject(LanguageRedirectionFacadeService);
  readonly flyoutLangSelectorFacade = inject(FlyoutLangSelectorFacade);
  readonly content = inject(HeaderFooterMachCoreContentService);
  readonly flightsWidgetFacade = inject(BookingFlightsFacadeService);
  readonly navSlimService = inject(NavSlimService);

  readonly loginModalService = inject(LoginModalService);
  readonly bodyScrollService = inject(BodyScrollService);
  readonly bookingTabService = inject(BookingTabService);
  readonly loginStateService = inject(LoginStateService);
  readonly darkSiteThemeService = inject(DarkSiteThemeService);
  readonly destroyRef = inject(DestroyRef);
  appConfig = inject(ApplicationConfig);
  elementRef = inject(ElementRef);
  flyoutLangSelectorLink = '';
  destroy$ = new Subject<void>();
  darkSiteLevel = '';
  darkSiteTheme = '';
  selectedPrimaryNav = '';
  darkSiteBlackThemeLogo = '';
  darkSiteLogo = '';

  isLoggedIn = this.loginStateService.isLoggedIn;
  triggerLoginModal = computed(() =>
    this.loginModalService.triggerLoginModal()
  );

  checkForAccountType = () =>
    this.accountManagement.checkForAccountType(this.destroyRef);

  hideSecondaryDesktopNav = () => this.navigation.hideSecondaryDesktopNav();
  focusHamburgerIcon = () => this.domManipulation.focusHamburgerIcon();
  checkDisableLegacy = () => this.configuration.checkDisableLegacy();
  checkMachCoreLoginModal = () =>
    this.configuration.checkEnableMachCoreLoginModal();

  resetExpandPanel = (closedFromButton: boolean) =>
    this.headerState.resetExpandPanel(closedFromButton);

  updateScreenType = () => this.screenDetection.updateScreenType();
  toggleMenuState = () => this.headerState.toggleMenuState();
  displayCloseIcon = (shopHeader = false) =>
    this.headerState.setDisplayCloseIcon(shopHeader);

  get isBusinessAccount(): boolean {
    return this.accountManagement.isBusiness;
  }

  headerFooterContent = toSignal<HeaderFooterMachCore | undefined>(
    this.content.getHeaderFooterMachCoreContent$()
  );

  constructor() {
    effect(() => {
      const myTrips = this.headerFooterContent()?.header?.my_trips;
      if (myTrips) {
        untracked(() => this.navigation.showMyTrips(myTrips));
      }
    });

    effect(() => {
      const content = this.headerFooterContent();
      if (content) {
        this.darkSiteThemeService.processDarkSiteConfig(
          this.headerFooterContent,
          (config) => {
            this.darkSiteLevel = config.level;
            this.darkSiteTheme = config.theme;
            this.darkSiteBlackThemeLogo = config.blackThemeLogo;
            this.darkSiteLogo = config.logo;
            this.selectedPrimaryNav = '';
            untracked(() => {
              if (this.darkSiteThemeService.isDarkSiteLevel(config.level))
                this.headerState.selectedNav.set(this.selectedPrimaryNav);

              if (this.darkSiteTheme === 'true')
                this.darkSiteThemeService.applyDarkThemeStyles();
            });
          }
        );
      }
    });
  }
}
